import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, MapPin, Phone, MessageCircle, Mail } from "lucide-react";
import { FORM } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Funding Solutions",
  "Mergers & Acquisitions",
  "Company Valuation",
  "Brand Valuation",
  "TEV Report",
  "Financial Advisory",
  "Business Research",
  "Other",
];

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service_interest: "",
  message: "",
};

export const ContactSection = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (form.name.trim().length < 2) return "Please share your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email address.";
    if (form.phone.replace(/\D/g, "").length < 7) return "Please enter a valid phone number.";
    if (!form.service_interest) return "Pick a service of interest.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      toast.error(v);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        company: form.company || null,
        message: form.message || null,
      };
      await axios.post(`${API}/leads`, payload);
      setSuccess(true);
      setForm(initial);
      toast.success("Thank you — a senior advisor will reach out shortly.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or WhatsApp us.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[#0d2017]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-overline text-brand-accent">Start a conversation</p>
            <h2 className="mt-4 font-serif text-white text-4xl md:text-5xl tracking-tight leading-tight">
              Let&rsquo;s discuss your <span className="italic text-brand-accent">next chapter.</span>
            </h2>
            <p className="mt-5 text-white/65 text-base leading-relaxed max-w-md">
              Share a few details and a senior advisor will reach out within one business day.
              All conversations are confidential.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md border border-brand-border bg-brand-surface flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-brand-accent" />
                </div>
                <div>
                  <p className="text-xs text-white/45 uppercase tracking-wider">Direct line</p>
                  <a
                    href="tel:+919959133388"
                    className="text-white text-base font-medium hover:text-brand-accent transition-colors"
                  >
                    +91 99591 33388
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md border border-brand-border bg-brand-surface flex items-center justify-center shrink-0">
                  <MessageCircle className="w-4 h-4 text-brand-accent" />
                </div>
                <div>
                  <p className="text-xs text-white/45 uppercase tracking-wider">WhatsApp</p>
                  <a
                    href="https://wa.me/919959133388"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white text-base font-medium hover:text-brand-accent transition-colors"
                  >
                    Chat with our team
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md border border-brand-border bg-brand-surface flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-brand-accent" />
                </div>
                <div>
                  <p className="text-xs text-white/45 uppercase tracking-wider">Email</p>
                  <a
                    href="mailto:info@transformo.co.in"
                    className="text-white text-base font-medium hover:text-brand-accent transition-colors"
                  >
                    info@transformo.co.in
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md border border-brand-border bg-brand-surface flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-brand-accent" />
                </div>
                <div>
                  <p className="text-xs text-white/45 uppercase tracking-wider">
                    Registered office
                  </p>
                  <p className="text-white text-sm leading-relaxed max-w-sm">
                    Bhanunidhi Business Solutions Pvt Ltd
                    <br />
                    #Away, 4th Floor, 10-50-58/1, Above ZUDIO Store,
                    <br />
                    Ramnagar, Waltair Main Road,
                    <br />
                    Visakhapatnam, Andhra Pradesh
                  </p>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={submit}
              noValidate
              data-testid={FORM.root}
              className="relative rounded-md border border-brand-border bg-brand-surface p-6 md:p-8"
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-white/5" />

              {success ? (
                <div
                  className="flex flex-col items-center justify-center py-10 text-center"
                  data-testid={FORM.success}
                >
                  <div className="w-14 h-14 rounded-full bg-brand-primary/15 border border-brand-primary/40 flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-brand-accent" />
                  </div>
                  <h3 className="mt-5 font-serif text-white text-2xl">Thank you.</h3>
                  <p className="mt-2 text-white/65 text-sm max-w-md">
                    Your enquiry has reached our desk. A senior advisor will be in touch within
                    one business day. For urgent matters, please WhatsApp 9959133388.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    className="mt-6 text-xs text-brand-accent hover:text-white transition-colors uppercase tracking-wider"
                  >
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Full name" required>
                      <Input
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="Jane Sharma"
                        data-testid={FORM.name}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/35 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
                      />
                    </Field>
                    <Field label="Company">
                      <Input
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        placeholder="Acme Industries Pvt Ltd"
                        data-testid={FORM.company}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/35 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
                      />
                    </Field>
                    <Field label="Email" required>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="you@company.com"
                        data-testid={FORM.email}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/35 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
                      />
                    </Field>
                    <Field label="Phone / WhatsApp" required>
                      <Input
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+91 99591 33388"
                        data-testid={FORM.phone}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/35 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
                      />
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Service interest" required>
                      <Select
                        value={form.service_interest}
                        onValueChange={(v) => update("service_interest", v)}
                      >
                        <SelectTrigger
                          data-testid={FORM.service}
                          className="bg-white/5 border-white/10 text-white focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
                        >
                          <SelectValue placeholder="Select an area" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#12251B] border-brand-border text-white">
                          {SERVICES.map((s) => (
                            <SelectItem
                              key={s}
                              value={s}
                              className="focus:bg-brand-primary/20 focus:text-white"
                            >
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Brief about your mandate">
                      <Textarea
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        rows={4}
                        placeholder="A few lines on your business, stage and what you're looking to achieve…"
                        data-testid={FORM.message}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/35 focus:border-brand-accent focus:ring-1 focus:ring-brand-accent resize-none"
                      />
                    </Field>
                  </div>

                  {error && (
                    <p
                      data-testid={FORM.error}
                      className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
                    >
                      {error}
                    </p>
                  )}

                  <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <p className="text-xs text-white/45 max-w-sm">
                      We respect your privacy. Information is used only to respond to your
                      enquiry and never shared.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid={FORM.submit}
                      className="btn-shine inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white rounded-md px-6 py-3 text-sm font-semibold transition-colors"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {submitting ? "Submitting…" : "Submit enquiry"}
                    </button>
                  </div>
                </>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.18em] text-white/60">
      {label} {required && <span className="text-brand-accent">*</span>}
    </Label>
    {children}
  </div>
);

export default ContactSection;
